import { create } from 'zustand';
import type { Workflow } from '@/stores/workflowStore';
import type { FlowTracingTruth } from '@/services/flowTracingTruth';

export interface FlowTracingSnapshot {
  id: string;
  workflowId: string;
  createdAt: string;
  source: 'edit' | 'run' | 'simulation';
  truth: Pick<FlowTracingTruth, 'state' | 'confidence' | 'score' | 'flowComplete' | 'label'>;
  workflow: Workflow;
}

interface FlowTracingHistoryState {
  history: Record<string, FlowTracingSnapshot[]>;
  pushSnapshot: (workflow: Workflow, truth: FlowTracingTruth, source: FlowTracingSnapshot['source']) => void;
  lastGoodSnapshot: (workflowId: string) => FlowTracingSnapshot | null;
  latestSnapshot: (workflowId: string) => FlowTracingSnapshot | null;
  compareToLastGood: (workflowId: string) => { confidenceDelta: number; nodeDelta: number; edgeDelta: number } | null;
}

const MAX_HISTORY = 40;
const genId = () => Math.random().toString(36).slice(2, 11);

export const useFlowTracingHistoryStore = create<FlowTracingHistoryState>((set, get) => ({
  history: {},

  pushSnapshot: (workflow, truth, source) =>
    set((state) => {
      const current = state.history[workflow.id] ?? [];
      const latest = current[current.length - 1];
      if (
        latest &&
        latest.truth.label === truth.label &&
        Math.abs(latest.truth.confidence - truth.confidence) < 0.01 &&
        latest.workflow.updatedAt?.toString?.() === workflow.updatedAt?.toString?.()
      ) {
        return state;
      }
      const snapshot: FlowTracingSnapshot = {
        id: genId(),
        workflowId: workflow.id,
        createdAt: new Date().toISOString(),
        source,
        truth: {
          state: truth.state,
          confidence: truth.confidence,
          score: truth.score,
          flowComplete: truth.flowComplete,
          label: truth.label,
        },
        workflow: structuredClone(workflow),
      };
      return {
        history: {
          ...state.history,
          [workflow.id]: [...current, snapshot].slice(-MAX_HISTORY),
        },
      };
    }),

  lastGoodSnapshot: (workflowId) => {
    const all = get().history[workflowId] ?? [];
    const good = [...all]
      .reverse()
      .find((s) => s.truth.state === 'match' && s.truth.confidence >= 0.6);
    return good ?? null;
  },

  latestSnapshot: (workflowId) => {
    const all = get().history[workflowId] ?? [];
    return all.length ? all[all.length - 1] : null;
  },

  compareToLastGood: (workflowId) => {
    const latest = get().latestSnapshot(workflowId);
    const good = get().lastGoodSnapshot(workflowId);
    if (!latest || !good) return null;
    return {
      confidenceDelta: latest.truth.confidence - good.truth.confidence,
      nodeDelta: latest.workflow.nodes.length - good.workflow.nodes.length,
      edgeDelta: latest.workflow.edges.length - good.workflow.edges.length,
    };
  },
}));
